'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';

type CommonTransitionProps = {
  color?: string;
  opacity?: number;
  backdropFilter?: string;
  transition?: string;
  /** Total animation duration in seconds (default: 1.0) */
  duration?: number;
  /** Delay before animation starts in seconds (default: 0) */
  delay?: number;
};

const hexToRgba = (hex: string, opacity: number): string => {
  let cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const CommonTransition = ({
  color = '#000000',
  opacity = 0.85,
  backdropFilter,
  transition = 'opacity 0.2s ease-in-out',
  duration = 1.0, // 👈 Default total duration
  delay = 0, // 👈 Default delay
}: CommonTransitionProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const backgroundColor = hexToRgba(color, opacity);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    const expandDuration = duration * 0.4;
    const collapseDuration = duration * 0.6;

    const tl = gsap.timeline({
      delay,
      onComplete: () => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('transition:done'));
        }
      },
    });

    tl.set(el, { scaleY: 1 });
    tl.to(el, {
      scaleY: 1,
      duration: expandDuration,
      ease: 'power2.out',
    });
    tl.to(el, {
      scaleY: 0,
      duration: collapseDuration,
      ease: 'power2.inOut',
    });

    return () => {
      tl.kill();
    };
  }, [duration, delay]);

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        pointerEvents: 'none',
        backgroundColor,
        backdropFilter,
        WebkitBackdropFilter: backdropFilter,
        transition,
        transformOrigin: 'top',
      }}
    />
  );
};

export default CommonTransition;
